import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { matchPath, useLocation, useNavigate } from "react-router-dom"
import type { CoreMessage } from "ai"
import { app } from "@/lib/app"
import { useTeamStore, switchToTeam } from "@/lib/hooks/useTeam"
import { useUser } from "@/lib/hooks/useUser"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { createModel, assertModelKey } from "@/lib/agents/utils"
import { AllAIModels } from "@/lib/agents/modellist"
import { pages } from "@/lib/pages"
import { ModuleRegistry } from "@/lib/assistant/registry"
import { runAssistant } from "@/lib/assistant/runtime"
import { buildToolSet, type ActionRecord } from "@/lib/assistant/policy"
import { buildSystemPrompt } from "@/lib/assistant/prompt"
import { toModelHistory } from "@/lib/assistant/history"
import { MODULE_CATALOG } from "@/lib/assistant/modules"
import { makeAppModule, navigablePages } from "@/lib/assistant/modules/app"
import { makeResearchModule, nativeSearchModel, RESEARCH_TOOL_LABELS } from "@/lib/assistant/modules/research"
import { makeFormsModule } from "@/lib/assistant/modules/forms"
import { makeChannelsModule } from "@/lib/assistant/modules/channels"
import { makeTeamsModule, type TeamSummary } from "@/lib/assistant/modules/teams"
import { redactSecrets, redactText } from "@/lib/assistant/redact"
import { mapTextBetweenTools } from "@/lib/assistant/interleave"
import { attachmentNote } from "@/lib/assistant/images"
import * as store from "@/lib/assistant/store"
import { AssistantContext, type AssistantContextValue, type PendingApproval } from "./context"
import type { ApprovalRequest, AssistantModule, DisplayMessage, DisplayToolRun, ToolPolicy, QuestionAnswer, QuestionRequest } from "@/lib/assistant/types"
import { useCorpora } from "@/pages/dev/smart-cms/shared/corpus/useCorpora"

export const DEFAULT_MODEL = "openai/gpt-5.6-luna"
export const MAX_STEPS = 40
const MODEL_KEY = "assistant-model"
const OPEN_KEY = "assistant-open"
// The chat to reopen after a reload the assistant asked for
const RESUME_KEY = "assistant-resume-chat"

// Pages and modules import these from ./context; re-exported for existing imports
export { useAssistant, useAssistantModule, type PendingApproval } from "./context"

function pageTitleFor(pathname: string): string | undefined {
  if (/^\/agent\/[^/]+$/.test(pathname)) return "Agent flow editor"
  for (const p of Object.values(pages)) {
    if (p.route && matchPath({ path: p.route, end: true }, pathname)) return p.title
  }
  return undefined
}

let idCounter = 0
const newId = () => `${Date.now().toString(36)}-${(idCounter++).toString(36)}`

export function AssistantProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const route = location.pathname + location.search
  const { selectedTeam } = useTeamStore()
  const teamId = selectedTeam?.id
  const { data: user } = useUser()
  const { hasPermission, canRead, assistant: perms } = usePermissions()

  const registry = useMemo(() => new ModuleRegistry(), [])
  const corpora = useCorpora(teamId ?? "", "assistant")

  const [open, setOpenState] = useState(() => {
    try { return localStorage.getItem(OPEN_KEY) === "true" } catch { return false }
  })
  const setOpen = useCallback((v: boolean) => {
    setOpenState(v)
    try { localStorage.setItem(OPEN_KEY, String(v)) } catch { /* private window */ }
  }, [])
  const [draft, setDraft] = useState("")

  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const messagesRef = useRef(messages)
  messagesRef.current = messages
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)

  const [conversationId, setConversationId] = useState<string | null>(null)
  const conversationIdRef = useRef<string | null>(null)
  conversationIdRef.current = conversationId
  const [conversations, setConversations] = useState<store.ConversationSummary[]>([])
  const [memory, setMemory] = useState<store.MemoryNote[]>([])
  const [skills, setSkills] = useState<store.AssistantSkill[]>([])
  const [persistence, setPersistence] = useState<"ok" | "missing">("ok")

  const [model, setModelState] = useState(() => {
    try {
      const saved = localStorage.getItem(MODEL_KEY)
      if (saved && AllAIModels.some(m => m.value === saved)) return saved
    } catch { /* private window */ }
    return DEFAULT_MODEL
  })
  const setModel = useCallback((m: string) => {
    setModelState(m)
    try { localStorage.setItem(MODEL_KEY, m) } catch { /* private window */ }
  }, [])

  const canAutoAccept = perms.review === "optional"
  const [autoAccept, setAutoAccept] = useState(false)
  const autoAcceptRef = useRef(false)
  autoAcceptRef.current = autoAccept && canAutoAccept
  useEffect(() => { if (!canAutoAccept) setAutoAccept(false) }, [canAutoAccept])

  const [approvals, setApprovals] = useState<PendingApproval[]>([])

  // Recent routes, for "where have they been" context
  const trailRef = useRef<string[]>([])
  useEffect(() => {
    const t = trailRef.current
    if (t[t.length - 1] !== location.pathname) trailRef.current = [...t, location.pathname].slice(-8)
  }, [location.pathname])

  // Latest values for tool closures, which outlive any one render
  const live = useRef({ route, teamId, canRead, hasPermission, perms })
  live.current = { route, teamId, canRead, hasPermission, perms }

  const [modulesTick, setModulesTick] = useState(0)
  useEffect(() => registry.subscribe(() => setModulesTick(t => t + 1)), [registry])
  const openModules = useMemo(() => registry.list(), [registry, modulesTick]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Approvals ──────────────────────────────────────────────────────────────

  const settle = useCallback((id: string, value: any) => {
    setApprovals(prev => {
      const hit = prev.find(a => a.id === id)
      hit?.resolve(value)
      return prev.filter(a => a.id !== id)
    })
  }, [])

  const requestApproval = useCallback((req: ApprovalRequest): Promise<boolean> => {
    if (autoAcceptRef.current) return Promise.resolve(true)
    setOpen(true)
    return new Promise<boolean>(resolve => {
      const id = newId()
      setApprovals(prev => [...prev, { id, kind: "confirm", req, fallback: false, resolve }])
    })
  }, [setOpen])

  const requestCustom = useCallback(<T,>(render: (done: (value: T) => void) => ReactNode, fallback: T): Promise<T> => {
    setOpen(true)
    return new Promise<T>(resolve => {
      const id = newId()
      setApprovals(prev => [...prev, {
        id, kind: "custom", fallback, resolve,
        render,
      }])
    })
  }, [setOpen])

  // Never auto-answered: auto-accept approves changes, it doesn't choose for the user
  const askQuestion = useCallback((question: QuestionRequest): Promise<QuestionAnswer | null> => {
    setOpen(true)
    return new Promise(resolve => {
      const id = newId()
      setApprovals(prev => [...prev, { id, kind: "question", question, fallback: null, resolve }])
    })
  }, [setOpen])

  const cancelApprovals = useCallback(() => {
    setApprovals(prev => {
      prev.forEach(a => a.resolve(a.fallback))
      return []
    })
  }, [])

  // ── Team data: chats, memory, skills ───────────────────────────────────────

  const refreshConversations = useCallback(async () => {
    if (!teamId) return
    const r = await store.listConversations(teamId)
    if (r.ok) setConversations(r.data)
    else if (r.missing) setPersistence("missing")
  }, [teamId])

  const refreshMemory = useCallback(async () => {
    if (!teamId) return
    const r = await store.listMemory(teamId)
    if (r.ok) setMemory(r.data)
  }, [teamId])

  const refreshSkills = useCallback(async () => {
    if (!teamId) return
    const r = await store.listSkills(teamId)
    if (r.ok) setSkills(r.data)
  }, [teamId])

  // Set when the assistant itself switches team: the chat and the running reply carry on
  const keepChatOnTeamChangeRef = useRef(false)
  // The reply finishes in the conversation it started in; the next message starts one in the new team
  const teamChangedDuringRunRef = useRef(false)

  useEffect(() => {
    const keep = keepChatOnTeamChangeRef.current
    keepChatOnTeamChangeRef.current = false
    if (keep) {
      teamChangedDuringRunRef.current = true
    } else {
      abortRef.current?.abort()
      setMessages([])
      setConversationId(null)
    }
    setConversations([])
    setMemory([])
    setSkills([])
    setPersistence("ok")
    if (!teamId || !perms.use) return
    void refreshConversations()
    void refreshMemory()
    void refreshSkills()
  }, [teamId, perms.use]) // eslint-disable-line react-hooks/exhaustive-deps

  const memoryRef = useRef(memory)
  memoryRef.current = memory
  const skillsRef = useRef(skills)
  skillsRef.current = skills

  // ── Always-on modules ──────────────────────────────────────────────────────

  const catalogForUser = () => MODULE_CATALOG.filter(c => !c.resource || live.current.canRead(c.resource))

  useAssistantModuleInternal(registry, "app", () => makeAppModule({
    navigate: to => navigate(to),
    getRoute: () => live.current.route,
    registry,
    pages: () => navigablePages(pages, live.current.canRead),
    catalog: catalogForUser(),
    canRead: r => live.current.canRead(r),
    teamId: () => live.current.teamId,
    canConfigure: () => live.current.perms.configure,
    skills: () => skillsRef.current,
    onMemoryChanged: () => { void refreshMemory() },
    askQuestion,
  }), perms.use)

  useAssistantModuleInternal(registry, "forms", () => makeFormsModule({
    teamId: live.current.teamId,
    onFormsPage: location.pathname.startsWith("/evaluations/forms"),
    ask: requestApproval,
  }), perms.use && canRead("forms"))

  useAssistantModuleInternal(registry, "channels", () => makeChannelsModule({
    teamId: live.current.teamId,
    ask: requestApproval,
  }), perms.use && canRead("apikeys"))

  const teamsRef = useRef<TeamSummary[]>([])
  teamsRef.current = ((user as any)?.teams ?? []) as TeamSummary[]

  useAssistantModuleInternal(registry, "teams", () => makeTeamsModule({
    currentTeamId: () => live.current.teamId,
    teams: () => teamsRef.current,
    switchTeam: async team => {
      keepChatOnTeamChangeRef.current = true
      switchToTeam(team as any)
      // Wait for the app to be in the new team, then for its pages to register their tools
      const until = Date.now() + 3000
      while (live.current.teamId !== team.id && Date.now() < until) await new Promise(r => setTimeout(r, 50))
      await registry.waitForChange(600)
    },
    ask: requestApproval,
  }), perms.use && ((user as any)?.teams?.length ?? 0) > 0)

  // ── Running a turn ─────────────────────────────────────────────────────────

  const labelsRef = useRef<Record<string, string>>({ ...RESEARCH_TOOL_LABELS })
  const labelFor = useCallback((name: string) => labelsRef.current[name] ?? name.replace(/_/g, " "), [])

  const patchAssistant = (id: string, fn: (m: DisplayMessage) => DisplayMessage) =>
    setMessages(prev => prev.map(m => (m.id === id ? fn(m) : m)))

  const updateRun = (id: string, callId: string, patch: Partial<DisplayToolRun> & { toolName?: string }) =>
    patchAssistant(id, m => {
      const tools = [...(m.tools ?? [])]
      const i = tools.findIndex(t => t.id === callId)
      if (i >= 0) tools[i] = { ...tools[i], ...patch }
      else tools.push({ id: callId, toolName: patch.toolName ?? "tool", args: patch.args ?? {}, status: patch.status ?? "preparing", result: patch.result })
      return { ...m, tools }
    })

  // An address to reload once the reply ends, set by reloadWhenDone
  const reloadRef = useRef<string | null>(null)
  const reloadWhenDone = useCallback((url: string) => {
    if (!busyRef.current) { window.location.assign(url); return }
    reloadRef.current = url
  }, [])

  const persistMessage = async (m: DisplayMessage, seq: number) => {
    const convId = conversationIdRef.current
    if (!convId || !teamId) return
    await store.appendMessage(convId, teamId, seq, m)
  }

  const send = useCallback(async (raw: string, images: string[] = []) => {
    const text = raw.trim() || (images.length ? "What do you see in this screenshot?" : "")
    if (!text || busyRef.current || !perms.use) return
    if (!teamId) {
      setMessages(prev => [...prev, { id: newId(), role: "assistant", content: "Select a team first." }])
      return
    }

    // /skill-name runs a team skill
    let prompt = text
    const slash = text.match(/^\/(\S+)\s*([\s\S]*)$/)
    if (slash) {
      const skill = skillsRef.current.find(s => s.enabled && s.name.toLowerCase() === slash[1].toLowerCase())
      if (skill) prompt = `Run the team skill "${skill.name}":\n${skill.instructions}\n\n${slash[2] || ""}`.trim()
    }

    const history = toModelHistory(messagesRef.current)
    const seq = messagesRef.current.length
    // This run gets the text as typed; what's shown, saved and replayed later has credentials hidden
    const userMsg: DisplayMessage = {
      id: newId(), role: "user", content: redactText(text), route: location.pathname,
      ...(images.length ? { images } : {}),
    }
    const replyId = newId()
    const reply: DisplayMessage = { id: replyId, role: "assistant", content: "", tools: [], isStreaming: true }
    setMessages(prev => [...prev, userMsg, reply])
    messagesRef.current = [...messagesRef.current, userMsg, reply]
    busyRef.current = true
    setBusy(true)
    const controller = new AbortController()
    abortRef.current = controller

    // Save the chat as it goes; a missing table just means it isn't saved
    if (!conversationIdRef.current && persistence === "ok") {
      const r = await store.createConversation(teamId, userMsg.content.slice(0, 80), location.pathname)
      if (r.ok) { conversationIdRef.current = r.data; setConversationId(r.data) }
      else if (r.missing) setPersistence("missing")
    }
    // Screenshots aren't saved: the saved chat keeps a note that they were there
    void persistMessage(images.length ? { ...userMsg, content: `${userMsg.content}\n\n${attachmentNote(images.length)}`, images: undefined } : userMsg, seq)

    // The run's own record, so the saved reply doesn't depend on React state having caught up
    let finalContent = ""
    const runs = new Map<string, DisplayToolRun>()
    const track = (callId: string, patch: Partial<DisplayToolRun>) => {
      const prev = runs.get(callId)
      runs.set(callId, { id: callId, toolName: "tool", args: {}, status: "preparing", ...prev, ...patch })
      updateRun(replyId, callId, patch)
    }
    try {
      const apiKeys = await app.fetchAPIkeys(teamId)
      assertModelKey(apiKeys, model)
      const native = nativeSearchModel(model, apiKeys)
      const llm = native?.model ?? createModel(apiKeys, model)
      if (!llm) throw new Error(`Model "${model}" isn't configured.`)

      const research = makeResearchModule({
        nativeWebTools: native?.tools, modelId: model, apiKeys,
        getCorpusIds: corpora.getAttachedIds, attachedCorpora: corpora.attachedCorpora,
      })

      const record = (a: ActionRecord) => {
        void store.recordAction({
          teamId, conversationId: conversationIdRef.current ?? undefined,
          module: a.module, tool: a.tool, args: a.args, status: a.status, route: live.current.route,
        })
      }

      const modulesNow = () => [...registry.list(), research]
      const tools = () => {
        const built = buildToolSet(modulesNow(), {
          can: (r, a) => live.current.hasPermission(r, a),
          review: live.current.perms.review,
          ask: (summary, detail) => requestApproval({ summary, detail }),
          record,
        })
        labelsRef.current = { ...labelsRef.current, ...built.labels }
        return built.tools
      }
      const system = () => {
        const mods = registry.list()
        return buildSystemPrompt({
          userName: [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email,
          teamName: (selectedTeam as any)?.name,
          route: live.current.route,
          pageTitle: pageTitleFor(window.location.pathname),
          trail: trailRef.current,
          pages: navigablePages(pages, live.current.canRead),
          catalog: catalogForUser(),
          modules: [...mods, research],
          skills: skillsRef.current,
          memory: memoryRef.current,
          review: live.current.perms.review,
          autoAccept: autoAcceptRef.current,
          nativeSearch: !!native,
        })
      }

      const result = await runAssistant({
        model: llm,
        system,
        tools,
        messages: [...history, (images.length
          ? { role: "user", content: [{ type: "text", text: prompt }, ...images.map(image => ({ type: "image", image }))] }
          : { role: "user", content: prompt }) as CoreMessage],
        maxSteps: MAX_STEPS,
        signal: controller.signal,
        onEvent: e => {
          if (e.type === "text") {
            finalContent += e.delta
            patchAssistant(replyId, m => ({ ...m, content: m.content + e.delta }))
          } else if (e.type === "tool-start") {
            // Where in the text it was called, so the panel shows it there
            track(e.id, { toolName: e.name, status: "preparing", at: finalContent.length })
          } else if (e.type === "tool-call") {
            track(e.id, { toolName: e.name, args: redactSecrets(e.args), status: "executing", at: runs.get(e.id)?.at ?? finalContent.length })
          } else if (e.type === "tool-result") {
            const failed = !!(e.result && typeof e.result === "object" && "error" in (e.result as any))
            track(e.id, { status: failed ? "error" : "complete", result: redactSecrets(e.result) })
          }
        },
      })
      if (result.hitStepLimit) {
        const note = `\n\n_Stopped after ${MAX_STEPS} steps. Say "continue" to keep going._`
        finalContent += note
        patchAssistant(replyId, m => ({ ...m, content: m.content + note }))
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        const msg = `Something went wrong: ${err?.message || "unknown error"}`
        finalContent += (finalContent ? "\n\n" : "") + msg
        patchAssistant(replyId, m => ({ ...m, content: m.content ? `${m.content}\n\n${msg}` : msg }))
      }
    } finally {
      cancelApprovals()
      abortRef.current = null
      busyRef.current = false
      setBusy(false)
      // A call that never got a result (stopped, or the run failed) shows as an error
      const settled = [...runs.values()].map(t => (t.status === "complete" || t.status === "error") ? t : { ...t, status: "error" as const })
      // Masked a stretch at a time, so each tool keeps its place in the text
      const { content, tools: finalTools } = mapTextBetweenTools(finalContent, settled, redactText)
      setMessages(prev => prev.map(m => (m.id === replyId ? { ...m, content, isStreaming: false, tools: finalTools } : m)))
      const persisted = persistMessage({ ...reply, content, tools: finalTools, isStreaming: false }, seq + 1)
      if (reloadRef.current) {
        const url = reloadRef.current
        reloadRef.current = null
        // Reopened after the reload, once the reply is saved
        try { if (conversationIdRef.current) sessionStorage.setItem(RESUME_KEY, conversationIdRef.current) } catch { /* private window */ }
        void persisted.catch(() => {}).finally(() => window.location.assign(url))
      }
      if (teamChangedDuringRunRef.current) {
        teamChangedDuringRunRef.current = false
        // This chat belongs to the old team; the next message starts a new one in the new team
        const finished = conversationIdRef.current
        conversationIdRef.current = null
        setConversationId(null)
        if (finished) void store.touchConversation(finished, { route: live.current.route })
      } else if (conversationIdRef.current) {
        void store.touchConversation(conversationIdRef.current, {
          route: live.current.route,
          context: { modules: registry.ids() },
        }).then(() => refreshConversations())
      }
    }
  }, [teamId, perms.use, model, persistence, user, selectedTeam, registry, corpora, requestApproval, cancelApprovals, refreshConversations, location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const stop = useCallback(() => {
    abortRef.current?.abort()
    cancelApprovals()
  }, [cancelApprovals])

  const newChat = useCallback(() => {
    if (busyRef.current) return
    setMessages([])
    setConversationId(null)
  }, [])

  const loadConversation = useCallback(async (id: string) => {
    if (busyRef.current) return
    const r = await store.loadMessages(id)
    if (!r.ok) return
    setMessages(r.data)
    setConversationId(id)
  }, [])

  const deleteConversation = useCallback(async (id: string) => {
    await store.deleteConversation(id)
    if (conversationIdRef.current === id) { setMessages([]); setConversationId(null) }
    void refreshConversations()
  }, [refreshConversations])

  const forgetMemory = useCallback(async (id: string) => {
    await store.deleteMemory(id)
    void refreshMemory()
  }, [refreshMemory])

  const saveSkill = useCallback(async (skill: Omit<store.AssistantSkill, "id"> & { id?: string }) => {
    if (!teamId) return null
    const sort = skill.id ? skillsRef.current.findIndex(s => s.id === skill.id) : skillsRef.current.length
    const r = await store.saveSkill(teamId, skill, Math.max(0, sort))
    void refreshSkills()
    return r.ok ? r.data : null
  }, [teamId, refreshSkills])

  const deleteSkill = useCallback(async (id: string) => {
    await store.deleteSkill(id)
    void refreshSkills()
  }, [refreshSkills])

  const openWith = useCallback((text?: string) => {
    setOpen(true)
    if (text) setDraft(text)
  }, [setOpen])

  // The chat that was open before a reloadWhenDone reload
  useEffect(() => {
    if (!teamId) return
    let id: string | null = null
    try { id = sessionStorage.getItem(RESUME_KEY); sessionStorage.removeItem(RESUME_KEY) } catch { /* private window */ }
    if (id) void loadConversation(id)
  }, [teamId, loadConversation])

  const value: AssistantContextValue = {
    registry, enabled: perms.use, open, setOpen, openWith, draft, setDraft,
    messages, busy, send, stop, newChat,
    conversationId, conversations, loadConversation, deleteConversation,
    memory, forgetMemory, skills, saveSkill, deleteSkill, persistence,
    model, setModel, autoAccept: autoAccept && canAutoAccept, setAutoAccept, canAutoAccept,
    canConfigure: perms.configure,
    approvals, resolveApproval: settle, requestApproval, requestCustom, askQuestion, reloadWhenDone,
    labelFor, openModules, pageTitle: pageTitleFor(location.pathname), corpora,
  }

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

// Same as useAssistantModule, for modules the provider itself owns
function useAssistantModuleInternal(registry: ModuleRegistry, id: string, build: () => AssistantModule | null, active: boolean) {
  const buildRef = useRef(build)
  buildRef.current = build
  useEffect(() => {
    if (!active) return
    return registry.register(id, () => buildRef.current())
  }, [registry, id, active])
}
